import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";

import ScreenContainer from "../../Components/ScreenContainer";
import PageHeaderTabs from "../../Components/PageHeaderTabs";
import TreeFab, { TREE_FAB_CLEARANCE } from "../../Components/TreeFab";
import { useWorkOrderConfig } from "../../PagesContent/AssetManagement/WorkOrders/hooks/useWorkOrderConfig";
import { canRaiseAnyWorkOrder } from "../../PagesContent/AssetManagement/WorkOrders/workOrderHelpers";
import {
    buildNewWorkOrderTree,
    workOrderRouteFromSelection,
} from "../../PagesContent/AssetManagement/WorkOrders/newWorkOrderTree";
import { usePageMeta } from "../../Hooks/usePageMeta";
import { usePermissions } from "../../Context/PermissionContext";
import { FEATURE_CODES } from "../../Static/FeatureMapping";
// The tab vocabulary lives in its own module so NewWorkOrderPage can read it
// without pulling all three tab contents into its bundle.
import { TAB_FEATURE, tabOffersNewWorkOrder } from "./assetManagementTabs";
import NetworkTabContent from "../../PagesContent/AssetManagement/Network/NetworkTabContent";
import AssetsTabContent from "../../PagesContent/AssetManagement/Assets/AssetsTabContent";
import WorkOrdersTabContent from "../../PagesContent/AssetManagement/WorkOrders/WorkOrdersTabContent";
import { FORM_TOKENS } from "../../PagesContent/AssetManagement/WorkOrders/WorkOrderFormShared";
import { ASSET_MANAGEMENT_LABEL } from "../../Static/ModuleLabels";

// Asset Management module shell. Modelled on BatteryIntelligence.jsx.
//
// Three tabs, three INDEPENDENT features. A user may hold any one of them, so
// the strip is filtered rather than assumed -- see TAB_FEATURE below. Overview
// lands later as a further entry in metaInfo.tabsConfig, which is why the strip
// reads from config rather than a local constant.

const FALLBACK_TABS = [{ slug: "network", name: "Network" }];

// Module level, so the array identity is stable and the config hook is not
// re-triggered by a fresh literal on each render.
//
// BOTH TABLES, where this used to ask for workOrderType alone.
//
// The old argument was that the shell needed the type list only to decide
// whether to offer the button, so pulling movementType's two JSON columns on
// every visit to every tab bought nothing. That changed when the button became
// a tree: its third level IS the movement types, so the control the user first
// touches now needs them. The form still fetches both when it opens; this is
// one more table on a request the shell was already making, not a new request.
const RAISE_GATE_CONFIG_TYPES = ["workOrderType", "movementType"];

/**
 * Which tabs this user sees: declare -> subset -> permission.
 *
 * Exported so the three stages can be tested against a fake hasPermission rather
 * than by mounting the page. Every branch here exists because of a failure mode
 * described in the comments at the call site.
 */
export const resolveVisibleTabs = ({ tabsConfig, tabsToShow, hasPermission }) => {
    const declared = tabsConfig?.length ? tabsConfig : FALLBACK_TABS;

    // tabsToShow drives the ORDER as well as the membership, so the org decides
    // which tab a user lands on -- the page opens on the first VISIBLE tab, and
    // that is tabs[0] below. Filtering `declared` instead would silently keep
    // pageMeta's order, and an org that put Assets first would still land on
    // Network with nothing explaining why.
    //
    // Mapping over tabsToShow also drops an unknown slug for free: a typo in a
    // hand-edited org row yields undefined, which the Boolean filter removes.
    const subset = Array.isArray(tabsToShow) && tabsToShow.length
        ? tabsToShow
              .map((slug) => declared.find((t) => t.slug === slug))
              .filter(Boolean)
        : declared;

    const safe = subset.length > 0 ? subset : declared;

    return safe.filter((t) => {
        const feature = TAB_FEATURE[t.slug];
        return !feature || hasPermission(feature, "view");
    });
};

// The landing tab for ?tab=<slug>, or null to keep the existing behaviour.
//
// Validated against the ALREADY RESOLVED tab list, which is the output of
// resolveVisibleTabs -- so this is checked against tabsToShow and the user's
// permissions in one step, and neither can be bypassed by typing a slug into
// the address bar. An unknown, unpermitted, hidden or absent slug returns null
// and the caller falls back to the first visible tab; nothing renders empty.
//
// Why there is a deep link at all: the New Work Order page has its own URL now,
// and saving returns here. Without this, `resolveVisibleTabs` lands on the
// first visible tab -- Network for most users -- and the maker never sees the
// row they just created.
export const resolveRequestedTab = (requestedSlug, tabs) => {
    if (!requestedSlug || typeof requestedSlug !== "string") return null;
    const match = (Array.isArray(tabs) ? tabs : []).find(
        (tab) => tab?.slug === requestedSlug
    );
    return match ? match.slug : null;
};

const AssetManagement = () => {
    const { pagesContent } = usePageMeta("AssetManagement");
    const { hasPermission } = usePermissions();
    const [searchParams] = useSearchParams();
    const requestedTab = searchParams.get("tab");
    const navigate = useNavigate();

    // The gate on the floating button. Shared with the Work Orders tab through
    // canRaiseAnyWorkOrder rather than recomputed -- see the note there.
    const { workOrderTypes, movementTypes } = useWorkOrderConfig(RAISE_GATE_CONFIG_TYPES);
    const canRaiseWorkOrder = useMemo(
        () => canRaiseAnyWorkOrder(workOrderTypes, hasPermission),
        [workOrderTypes, hasPermission]
    );

    // THE ANSWERS ARE GIVEN BEFORE THE PAGE OPENS, not on the form.
    //
    // They are decisions taken once, and the page that used to ask them was
    // already built for the answer -- so a maker chose between "Single asset"
    // and "Paste many" while looking at a form that had picked one for them.
    // The FAB asks instead, and the page renders the answer.
    //
    // They ride in router state beside the serials, for the same reason they
    // do: this is state about a navigation, not something to put in a URL.
    const openNewWorkOrder = useCallback(
        (selection) =>
            navigate("/asset-management/work-orders/new", {
                state: workOrderRouteFromSelection(selection),
            }),
        [navigate]
    );

    // The tree the FAB walks, BUILT FROM THE ORG'S OWN movementType rows --
    // see buildNewWorkOrderTree. Null until the config lands, which is what
    // keeps the button from opening onto a level with nothing in it.
    const newWorkOrderTree = useMemo(
        () => buildNewWorkOrderTree(movementTypes),
        [movementTypes]
    );

    // Declare -> subset -> permission. Three stages, three different owners:
    // tabsConfig says what tabs EXIST and what they are called, tabsToShow says
    // which of them this org uses, and RBAC says which of those this user may
    // open. Only the last is enforced -- see the note on tabsToShow above.
    //
    // tabsToShow comes off pageMeta, NOT metaInfo. usePageMeta assigns the whole
    // merged payload to `pageMeta` (usePageMeta.js:66) and never sets a `metaInfo`
    // key; every other page reads metaInfo.tabsToShow only because each has its own
    // bespoke loader. Copying their path here yields undefined, which resolves to
    // "all tabs" -- so it would look correct until an org actually tried to subset.
    const tabs = useMemo(
        () => resolveVisibleTabs({
            tabsConfig: pagesContent?.tabsConfig,
            tabsToShow: pagesContent?.pageMeta?.tabsToShow,
            hasPermission,
        }),
        [pagesContent, hasPermission]
    );

    const [selectedTab, setSelectedTab] = useState(null);

    // How many rows the Assets tab has ticked. Held HERE because the floating
    // button is rendered here and has to stand down while a selection exists;
    // the rows themselves stay in the tab that owns them.
    const [assetSelectionCount, setAssetSelectionCount] = useState(0);

    // Whether the SELECTION is the user's or ours. Until they click a tab, the
    // one on screen is a default we chose, and a default stays open to
    // correction when better information arrives.
    const [tabWasChosen, setTabWasChosen] = useState(false);

    // Adjust-state-on-prop-change rather than an effect: tabs arrive with the
    // meta, and the first render after they land must already have a valid
    // selection.
    //
    // THE TAB LIST ARRIVES IN TWO STAGES. Before pageMeta loads, tabsConfig is
    // empty and resolveVisibleTabs falls back to Network alone, so the default
    // computed then is "network". Correcting only an INVALID selection is not
    // enough: when the real config lands with Assets first, network is still a
    // visible tab, so a validity check passes and the stale default sticks. The
    // page opened on Network for every org, whatever tabsToShow said.
    //
    // So an unchosen selection is re-derived whenever the default changes, and
    // a chosen one is left alone. `tabWasChosen` is what separates them; it is
    // set by the tab strip's onChange and by nothing else. There is no loop
    // because once selectedTab equals autoTab the condition is false.
    //
    // ?tab=<slug> feeds the same default, so a deep link survives the two-stage
    // load as well. An unusable slug resolves to null and tabs[0] stands.
    const autoTab =
        tabs.length > 0
            ? resolveRequestedTab(requestedTab, tabs) || tabs[0].slug
            : null;
    const selectionIsValid = tabs.some((t) => t.slug === selectedTab);
    if (autoTab && (!selectionIsValid || (!tabWasChosen && selectedTab !== autoTab))) {
        setSelectedTab(autoTab);
    }

    const handleTabChange = (slug) => {
        setTabWasChosen(true);
        setSelectedTab(slug);
    };

    // RBAC v2, not the `permissions` route prop: that prop is a STRING ARRAY
    // (Login.js adminPermissions / userPermissions), so `permissions?.delete`
    // was always undefined and the approver-only action was unreachable for
    // everyone. hasPermission returns true under RBAC v1, which keeps v1 orgs
    // working.
    const canCreate = hasPermission(FEATURE_CODES.ASSET_MANAGEMENT_NETWORK_ENTITIES, "create");
    const canEdit = hasPermission(FEATURE_CODES.ASSET_MANAGEMENT_NETWORK_ENTITIES, "update");
    // `delete` is granted and enforced end to end, but no row action exposes it
    // yet -- see the note on customRenderers in NetworkTabContent.

    // Assets carry update and delete here, and NOT create: an asset is born in
    // a Vendor Intake work order now, so ASSETS:create gates nothing on this
    // screen and reading it would suggest a button that no longer exists.
    // Work orders deliberately have no check here either -- the queue needs
    // only WORK_ORDERS:view, which TAB_FEATURE already gated, and raising or
    // approving one is granted PER TYPE (WORK_ORDER.<TYPE>), which is what
    // canRaiseWorkOrder above resolves.
    const assetsCanEdit = hasPermission(FEATURE_CODES.ASSET_MANAGEMENT_ASSETS, "update");
    const assetsCanDelete = hasPermission(FEATURE_CODES.ASSET_MANAGEMENT_ASSETS, "delete");

    const activeTab = tabs.find((t) => t.slug === selectedTab) || tabs[0];

    // TWO gates, and both have to pass: the user may raise a work order at
    // all, and the tab they are standing on is one that offers the action.
    // Network manages entities rather than moving assets, so the button was
    // out of context there -- see WORK_ORDER_ACTION_TABS, which lists what
    // offers it rather than negating what does not.
    // THREE gates now. With rows ticked, the Assets table draws its own
    // "+ New Work Order" in the header, right beside the "N selected" count,
    // and that button acts on the ticked rows. The floating one starts BLANK.
    // Two buttons with the same words and different meanings, on screen at the
    // same time, is a coin toss the user has no way to call -- so the blank
    // one stands down while a selection exists, and comes back when it is
    // cleared.
    const showNewWorkOrder =
        canRaiseWorkOrder
        && tabOffersNewWorkOrder(activeTab?.slug)
        && assetSelectionCount === 0;

    return (
        <ScreenContainer
            pagesContent={{ ...pagesContent, metaInfo: pagesContent?.pageMeta }}
        >
            <Box
                sx={{
                    position: "sticky",
                    top: "50px",
                    zIndex: 1000,
                    backgroundColor: FORM_TOKENS.panel,
                }}
            >
                <PageHeaderTabs
                    title={pagesContent?.pageHeader || ASSET_MANAGEMENT_LABEL}
                    tabs={tabs}
                    selectedTab={selectedTab}
                    onChange={handleTabChange}
                    aria-label={`${ASSET_MANAGEMENT_LABEL} sections`}
                />
            </Box>

            {/* px:3 matches the page title in PageHeaderTabs. Without it the
                tab body -- the table card included -- ran edge to edge: flush
                against the sidebar on the left and clipped on the right, while
                the header above it sat inset. Every child inset now comes from
                here, so there is one number to change. */}
            <Box
                sx={{
                    px: 3,
                    pt: 2,
                    // Room reserved for the floating button, which is out of
                    // flow and cannot push anything: without this it sits on
                    // top of the last table row and the pagination control at
                    // short viewport heights.
                    // Reserved only where the button is actually drawn, or
                    // the Network tab keeps a gap for something that is not
                    // there.
                    pb: showNewWorkOrder ? TREE_FAB_CLEARANCE : 3,
                }}
            >
                {!pagesContent?.mounted ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <>
                        {activeTab?.slug === "network" && (
                            <NetworkTabContent
                                // pagesContent is usePageMeta's WRAPPER; the
                                // merged pageMeta/orgMeta payload is one level
                                // down.
                                metaInfo={pagesContent?.pageMeta}
                                canCreate={canCreate}
                                canEdit={canEdit}
                            />
                        )}
                        {activeTab?.slug === "assets" && (
                            <AssetsTabContent
                                metaInfo={pagesContent?.pageMeta}
                                // Reported up for ONE reason: the floating
                                // button hides while rows are ticked. The
                                // selection itself stays in the tab that owns
                                // it -- this is a count, not the rows.
                                onSelectionCountChange={setAssetSelectionCount}
                                canEdit={assetsCanEdit}
                                canDelete={assetsCanDelete}
                                // The same predicate the floating button uses,
                                // passed down rather than resolved a second
                                // time from a second config fetch.
                                canRaiseWorkOrder={canRaiseWorkOrder}
                                // So a work order raised from here can send
                                // the user BACK here, under the name this org
                                // actually calls the tab.
                                tabSlug={activeTab?.slug}
                                tabLabel={activeTab?.name}
                            />
                        )}
                        {activeTab?.slug === "work-orders" && (
                            <WorkOrdersTabContent
                                metaInfo={pagesContent?.pageMeta}
                            />
                        )}
                        {tabs.length === 0 && (
                            <Box sx={{ py: 8, textAlign: "center", color: "text.secondary" }}>
                                You do not have access to any section of {ASSET_MANAGEMENT_LABEL}.
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* On EVERY tab of the module, not just the queue. Raising a work
                order is what someone does after looking at an asset or a
                network entity, and a control that only exists on the tab they
                are not standing on is a control they do not find.

                It lives here because there is no dialog state left to hoist --
                the form is a page now, so this is a navigation and nothing
                more. Nothing else in this module is pinned bottom-right, and
                it sits below MUI's modal layer so the detail and review
                dialogs cover it. */}
            {showNewWorkOrder && newWorkOrderTree && (
                <TreeFab
                    testId="asset-management-new-work-order"
                    tree={newWorkOrderTree}
                    position="bottom-right"
                    // ONE WALK, TWO ANSWERS: how many assets, then which
                    // movement. Both used to be asked by a menu and the second
                    // one was not asked at all -- the movement type was the
                    // first field of the form. Asking it here means the form
                    // opens on a decision already made, and the breadcrumb
                    // keeps both answers visible while they are being made.
                    onFinish={openNewWorkOrder}
                />
            )}
        </ScreenContainer>
    );
};

export default AssetManagement;
